import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Briefcase,
  CalendarRange,
  Clock,
  Filter,
  Loader2,
  MapPin,
  Pencil,
  Share2,
  Trash2,
  UserSearch,
  Users,
  Wallet,
  CircleOff,
} from "lucide-react";
import { getProjectbyID } from "reducer/projectsSlice";
import { API } from "service/Interceptor";
import { decodeBriefPlainText, serverFiles } from "utils/utils";
import {
  briefApplicantsPath,
  isApplicationsClosed,
  setBriefApplicationsClosed,
} from "utils/creatorBriefs";
import toast from "components/Toast/toast";
import ApplyDialog from "components/app/ApplyDialog";
import ReportModal from "components/UI/ReportModal/ReportModal";
import { useAppliedJobIds } from "utils/appliedJobs";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const stripHtml = (value = "") => decodeBriefPlainText(value);

const parseBriefExtras = (description = "") => {
  const text = stripHtml(description);
  return {
    work: text.match(/Work style:\s*([^\n]+)/i)?.[1]?.trim() || null,
    looking: text.match(/Looking for:\s*([^\n]+)/i)?.[1]?.trim() || null,
    city: text.match(/City:\s*([^\n]+)/i)?.[1]?.trim() || null,
    timeline: text.match(/Timeline:\s*([^\n]+)/i)?.[1]?.trim() || null,
    dates: text.match(/Project dates:\s*([^\n]+)/i)?.[1]?.trim() || null,
    needLine:
      text
        .split("\n")
        .map((line) => line.trim())
        .find(
          (line) =>
            line &&
            !/^(looking for|work style|city|budget|timeline|project dates|industry|jobid|applicationsclosed):/i.test(
              line
            )
        ) || null,
  };
};

const formatBudget = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 100000)
    return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000) return `₹${Math.round(num / 1000)}k`;
  return `₹${num}`;
};

const formatDuration = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (!Number.isNaN(num) && num > 0) {
    return `${num} day${num === 1 ? "" : "s"}`;
  }
  return String(n);
};

const formatProjectDates = (start, end, fromDescription) => {
  if (fromDescription) return fromDescription;
  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
  const a = fmt(start);
  const b = fmt(end);
  if (a && b) return a === b ? a : `${a} → ${b}`;
  return a || b || null;
};

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const SingleProjectPage = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { project, isLoading } = useSelector((state) => state.projects);
  const { userProfile } = useSelector((state) => state.userProfile);
  const { hasApplied } = useAppliedJobIds();

  const [applyOpen, setApplyOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    dispatch(getProjectbyID(projectId));
  }, [dispatch, projectId]);

  const isBrand = userProfile?.userType === "BUSINESS";
  const isOwner = useMemo(() => {
    if (!project || !userProfile) return false;
    if (isBrand) {
      const myCompanyId =
        userProfile?.companyResponseDto?.id ||
        userProfile?.companyResponseDto?.companyId;
      const jobCompanyId =
        project?.company?.id ||
        project?.companyJobRespDto?.id ||
        project?.companyId;
      if (myCompanyId && jobCompanyId) {
        return String(myCompanyId) === String(jobCompanyId);
      }
      // Brands listing is company-scoped — treat as owner when brand
      return true;
    }
    return false;
  }, [project, userProfile, isBrand]);

  const brand =
    project?.companyJobRespDto?.name || project?.company?.name || "Brand";
  const logoPath =
    project?.companyJobRespDto?.logo || project?.company?.logo;
  const avatarSrc = logoPath ? serverFiles(logoPath) : null;
  const industry =
    (Array.isArray(project?.industry) && project.industry[0]) ||
    project?.category?.name ||
    null;

  const extras = parseBriefExtras(project?.description);
  const needText =
    extras.needLine ||
    decodeBriefPlainText(project?.jobTitle || "") ||
    stripHtml(project?.description) ||
    "Open brief";
  const creativeType =
    (Array.isArray(project?.skillsRequired) &&
      project.skillsRequired.filter(Boolean).join(", ")) ||
    extras.looking ||
    null;
  const workStyle =
    project?.workStyle || project?.work_style || extras.work || null;
  const cityRaw =
    extras.city || project?.cityName || project?.city?.name || null;
  const city = cityRaw ? String(cityRaw).split(",")[0].trim() : null;
  const budget = formatBudget(project?.projectBudget);
  const timeline = extras.timeline || formatDuration(project?.projectDuration);
  const projectDates = formatProjectDates(
    project?.startDate,
    project?.endDate,
    extras.dates
  );
  const applied =
    !!project?.isApplied ||
    hasApplied(project?.jobId) ||
    hasApplied(projectId);
  const applicationsClosed = isApplicationsClosed(project);

  const metaRows = [
    { key: "looking", Icon: UserSearch, label: "Looking for", value: creativeType },
    { key: "work", Icon: Filter, label: "Work style", value: workStyle },
    { key: "city", Icon: MapPin, label: "City", value: city || null },
    { key: "budget", Icon: Wallet, label: "Budget", value: budget },
    {
      key: "dates",
      Icon: CalendarRange,
      label: "Project dates",
      value: projectDates,
    },
    { key: "timeline", Icon: Clock, label: "Timeline", value: timeline },
  ].filter((row) => row.value);

  const profileHref = project?.company?.userAliasId
    ? `/user-profile/${project.company.userAliasId}`
    : "#";

  const deleteBrief = async () => {
    if (
      !window.confirm(
        "Delete this brief? Applications on it will also be removed."
      )
    )
      return;
    setDeleting(true);
    try {
      await API.delete(`api/jobs/${project.jobId}`);
      toast.success("Brief deleted");
      navigate("/projects");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to delete"
      );
    } finally {
      setDeleting(false);
    }
  };

  const toggleClosed = async () => {
    try {
      await setBriefApplicationsClosed(project, !applicationsClosed);
      toast.success(
        applicationsClosed ? "Applications reopened" : "Applications closed"
      );
      dispatch(getProjectbyID(projectId));
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not update brief"
      );
    }
  };

  if (isLoading && !project) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-20 text-center text-zinc-500 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Loading brief…
      </div>
    );
  }

  if (!project) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-20 text-center text-zinc-500">
        <p>This brief could not be found.</p>
        <Link
          to="/projects"
          className="mt-4 inline-flex rounded-full bg-[#6C63F5] text-white px-4 py-2 text-sm font-semibold hover:bg-[#5751E5]"
        >
          Back to projects
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/projects"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100 transition-colors mb-6"
      >
        <ArrowLeft size={16} /> Back to projects
      </Link>

      <motion.article
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 sm:p-7"
      >
        <div className="flex items-center gap-3">
          <Link to={profileHref} className="shrink-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={brand}
                className="h-12 w-12 rounded-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = NO_IMAGE;
                }}
              />
            ) : (
              <div className="avatar-fallback h-12 w-12 rounded-full flex items-center justify-center font-semibold">
                {initials(brand)}
              </div>
            )}
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-display font-semibold text-zinc-50 truncate">
                {brand}
              </span>
              <span className="rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5">
                Brand
              </span>
            </div>
            <div className="text-xs text-zinc-500">Open brief</div>
          </div>
          {industry && (
            <span className="ml-auto rounded-full bg-[#6C63F5]/10 border border-[#6C63F5]/25 text-[#8b83ff] text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-1 shrink-0">
              {String(industry).split(",")[0]}
            </span>
          )}
        </div>

        <h1 className="mt-5 font-display font-bold text-2xl tracking-tight text-zinc-50 leading-snug">
          {needText}
        </h1>

        <div className="mt-5 space-y-2.5 text-sm">
          {metaRows.map(({ key, Icon, label, value }) => (
            <div key={key} className="flex items-center gap-2 text-zinc-400">
              <Icon size={15} className="text-[#8b83ff] shrink-0" />
              {label} <span className="text-zinc-200">{value}</span>
            </div>
          ))}
        </div>

        <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied to clipboard");
              } catch {
                toast.error("Could not copy link");
              }
            }}
            className="rounded-full border border-zinc-700 text-zinc-200 py-3 px-4 text-sm font-semibold hover:border-zinc-500 inline-flex items-center justify-center gap-2"
          >
            <Share2 size={15} className="text-[#8b83ff]" /> Share brief
          </button>
          {isOwner ? (
            <>
              <button
                type="button"
                onClick={() => navigate(briefApplicantsPath(project))}
                className="flex-1 rounded-full bg-[#6C63F5] text-white py-3 px-4 text-sm font-semibold hover:bg-[#5751E5] inline-flex items-center justify-center gap-2"
              >
                <Users size={15} /> View applicants
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate(`/projects/edit-project/${project.jobId}`)
                }
                className="flex-1 rounded-full border border-zinc-700 text-zinc-200 py-3 px-4 text-sm font-semibold hover:border-zinc-500 inline-flex items-center justify-center gap-2"
              >
                <Pencil size={14} /> Edit brief
              </button>
              <button
                type="button"
                onClick={toggleClosed}
                className="flex-1 rounded-full border border-zinc-700 text-zinc-200 py-3 px-4 text-sm font-semibold hover:border-zinc-500 inline-flex items-center justify-center gap-2"
              >
                <CircleOff size={14} />
                {applicationsClosed ? "Reopen applications" : "Close applications"}
              </button>
              <button
                type="button"
                onClick={deleteBrief}
                disabled={deleting}
                className="rounded-full border border-red-500/30 text-red-300 py-3 px-4 text-sm font-semibold hover:border-red-500/50 inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete
              </button>
            </>
          ) : applied ? (
            <button
              type="button"
              disabled
              className="w-full rounded-full bg-[#6C63F5] text-white py-3 text-sm font-semibold opacity-40 cursor-not-allowed"
            >
              Applied
            </button>
          ) : isBrand ? (
            <div className="w-full rounded-full bg-zinc-900 border border-dashed border-zinc-800 py-3 text-xs text-zinc-500 inline-flex items-center justify-center gap-2">
              Only creatives can apply
            </div>
          ) : applicationsClosed ? (
            <button
              type="button"
              disabled
              className="w-full rounded-full bg-zinc-800 text-zinc-400 py-3 text-sm font-semibold opacity-70 cursor-not-allowed"
            >
              Application Closed
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setApplyOpen(true)}
                className="flex-1 rounded-full bg-[#6C63F5] text-white py-3 text-sm font-semibold hover:bg-[#5751E5] inline-flex items-center justify-center gap-2"
              >
                <Briefcase size={15} /> Apply
              </button>
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="rounded-full border border-zinc-700 text-zinc-300 py-3 px-4 text-sm font-semibold hover:border-zinc-500"
              >
                Report
              </button>
            </>
          )}
        </div>
      </motion.article>

      <AnimatePresence>
        {applyOpen && (
          <ApplyDialog
            project={project}
            onClose={() => setApplyOpen(false)}
            onApplied={() => {
              setApplyOpen(false);
              dispatch(getProjectbyID(projectId));
            }}
          />
        )}
      </AnimatePresence>

      {reportOpen && (
        <ReportModal
          id={project?.jobId}
          type="Project"
          reportModal={reportOpen}
          setReportModal={setReportOpen}
        />
      )}
    </div>
  );
};

export default SingleProjectPage;
